import React, { memo, useCallback, useMemo } from 'react'
import type { ComposedInvoice, PendingInvoiceAction } from 'src/interfaces/invoice'
import { InvoiceActionVerb } from 'src/constants/invoice/enums'
import { buildInvoiceLabels, getComposedInvoiceLabel } from 'src/utils/invoiceLabels'
import { getAvailableComposedInvoiceActions, getTargetInvoiceIds } from 'src/utils/invoiceActions'
import { countScheduledPaymentReleases } from 'src/utils/invoicePaymentRelease'
import { InvoiceActionsMenu } from './InvoiceActionsMenu'

interface Props {
  invoice: ComposedInvoice
  onRunAction(action: PendingInvoiceAction): void
}

/**
 * Money verbs only. A payment-release hold belongs to a single partner invoice, so Schedule,
 * Reschedule and Cancel live on the partner invoice rows of the details page — never here.
 */
const ComposedInvoiceActionsCellComponent = ({ invoice, onRunAction }: Props) => {
  const verbs = useMemo(() => {
    return getAvailableComposedInvoiceActions(invoice)
  }, [invoice])

  // Hands a pending action up to the page; it never dispatches. The confirm modal is the only
  // thing that dispatches, so no money action can start without passing through it.
  const handleSelect = useCallback(
    (verb: InvoiceActionVerb) => {
      const invoiceIds = getTargetInvoiceIds(invoice, verb)

      onRunAction({
        verb,
        invoiceIds,
        composedInvoiceLabel: getComposedInvoiceLabel(invoice),
        invoiceLabels: buildInvoiceLabels(invoice.partnerInvoices),
        scheduledReleaseCount: countScheduledPaymentReleases(invoice.partnerInvoices, invoiceIds),
      })
    },
    [invoice, onRunAction],
  )

  return (
    <InvoiceActionsMenu
      verbs={verbs}
      dataTestId={`invoices-list-actions-${invoice.id}`}
      onSelect={handleSelect}
    />
  )
}

export const ComposedInvoiceActionsCell = memo(ComposedInvoiceActionsCellComponent)
